import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Spinner, Alert, Modal, Form, Pagination, Row, Col } from 'react-bootstrap';
import { fetchPartnersRequest, deletePartnerRequest } from '../../store/actions/partnerActions';
import { getAllPartners } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import PartnerCompanyAutocomplete from '../../components/Common/PartnerCompanyAutocomplete';
import usePersistedSearchState from '../../hooks/usePersistedSearchState';
import { FiLink, FiRefreshCw, FiPlus, FiEdit, FiTrash2, FiEye } from 'react-icons/fi';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminPartner.css';

const EMPTY_PARTNERS_FILTERS = {
    email: '',
    company_name: '',
    first_name: '',
    last_name: '',
};

const AdminPartner = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { partners, create, delete: deleteState } = useSelector((state) => state.partners);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedPartnerId, setSelectedPartnerId] = useState(null);
    const {
        filters,
        setFilters,
        appliedFilters,
        setAppliedFilters,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        companyPartnerSearch,
        setCompanyPartnerSearch,
        clearSearchState,
    } = usePersistedSearchState('partners', EMPTY_PARTNERS_FILTERS, {
        withCompanyPartnerSearch: true,
    });
    const [filterPartners, setFilterPartners] = useState([]);
    const [filterPartnersLoading, setFilterPartnersLoading] = useState(false);
    const [filterPartnersError, setFilterPartnersError] = useState(null);

    const partnerCompanyOptions = useMemo(() => {
        const names = filterPartners
            .map((partner) => partner.company_name?.trim())
            .filter((name) => name);
        return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, 'fr'));
    }, [filterPartners]);

    const loadPartners = useCallback((page = 1, filtersToUse = null) => {
        const skip = (page - 1) * itemsPerPage;
        const params = {
            skip,
            limit: itemsPerPage,
        };

        const activeFilters = filtersToUse !== null ? filtersToUse : appliedFilters;

        Object.keys(activeFilters).forEach((key) => {
            if (activeFilters[key] && activeFilters[key].trim() !== '') {
                params[key] = activeFilters[key].trim();
            }
        });

        dispatch(fetchPartnersRequest(params));
    }, [itemsPerPage, appliedFilters, dispatch]);

    useEffect(() => {
        loadPartners(currentPage);
    }, [currentPage, loadPartners]);

    useEffect(() => {
        const loadFilterPartners = async () => {
            try {
                setFilterPartnersLoading(true);
                setFilterPartnersError(null);
                const response = await getAllPartners();
                const partnersArray = response?.users && Array.isArray(response.users)
                    ? response.users
                    : (Array.isArray(response) ? response : []);
                setFilterPartners(partnersArray);
            } catch (error) {
                setFilterPartners([]);
                setFilterPartnersError(handleApiError(error, 'Erreur lors du chargement des partenaires'));
            } finally {
                setFilterPartnersLoading(false);
            }
        };

        loadFilterPartners();
    }, []);

    useEffect(() => {
        if (deleteState.success) {
            setShowDeleteModal(false);
            setSelectedPartnerId(null);
        }
    }, [deleteState.success]);

    useEffect(() => {
        if (create.success || deleteState.success) {
            loadPartners(currentPage);
        }
    }, [create.success, deleteState.success, currentPage, loadPartners]);

    const handleRefresh = () => {
        loadPartners(currentPage);
    };

    const handleCreate = () => {
        navigate('/admin/partner/create');
    };

    const handleDetails = (partnerId) => {
        navigate(`/admin/partner/${partnerId}`);
    };

    const handleEdit = (partnerId) => {
        navigate(`/admin/partner/edit/${partnerId}`);
    };

    const handleDelete = (partnerId) => {
        setSelectedPartnerId(partnerId);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = () => {
        dispatch(deletePartnerRequest(selectedPartnerId));
    };

    const partnersData = partners?.data?.data || partners?.data || [];
    const partnersArray = Array.isArray(partnersData) ? partnersData : [];
    const meta = partners?.data?.meta || {
        total: 0,
        skip: 0,
        limit: itemsPerPage,
        page: currentPage,
        total_pages: 0,
    };

    const totalPages = meta.total_pages || Math.max(1, Math.ceil(meta.total / itemsPerPage));
    const totalItems = meta.total || 0;
    const indexOfFirstItem = meta.skip || 0;
    const indexOfLastItem = Math.min(indexOfFirstItem + partnersArray.length, totalItems);

    const currentPartners = partnersArray;

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleApplyFilters = () => {
        const companyName = (filters.company_name || companyPartnerSearch).trim();
        const nextFilters = { ...filters, company_name: companyName };
        setFilters(nextFilters);
        setCompanyPartnerSearch(companyName);
        setAppliedFilters(nextFilters);
        setCurrentPage(1);
        loadPartners(1, nextFilters);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleApplyFilters();
        }
    };

    const handleClearFilters = () => {
        const emptyFilters = { ...EMPTY_PARTNERS_FILTERS };
        clearSearchState();
        setTimeout(() => {
            loadPartners(1, emptyFilters);
        }, 100);
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        loadPartners(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getPaginationRange = () => {
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        return { startPage, endPage };
    };

    return (
        <div className="dashboard-page admin-partner-page">
            {(create.error || deleteState.error) && (
                <Alert variant="danger" dismissible>
                    {create.error || deleteState.error}
                </Alert>
            )}

            {(create.success || deleteState.success) && (
                <Alert variant="success" dismissible>
                    Opération réussie !
                </Alert>
            )}

            <Card className="partner-main-card">
                <Card.Body>
                    <div className="page-header partner-page-header">
                        <div className="partner-page-header__title">
                            <h2 className="page-title">
                                <span className="page-title-icon">
                                    <FiLink />
                                </span>
                                Gestion des Partenaires
                            </h2>
                        </div>
                        <div className="d-flex gap-2 page-header-actions">
                            <Button variant="outline-primary" className="btn-refresh" onClick={handleRefresh} disabled={partners.loading}>
                                <FiRefreshCw className={partners.loading ? 'spinning' : ''} /> Actualiser
                            </Button>
                            <Button variant="primary" className="btn-create" onClick={handleCreate}>
                                <FiPlus /> Nouveau Partenaire
                            </Button>
                        </div>
                    </div>

                    {partners.error && (
                        <Alert variant="danger" className="mt-3">
                            {partners.error}
                        </Alert>
                    )}

                    <Card className="mb-3 partner-filters-card">
                        <Card.Header className="partner-filters-card__header d-flex justify-content-between align-items-center">
                            <h6 className="mb-0 partner-filters-card__title">Filtres de recherche</h6>
                            <div className="d-flex gap-2 partner-filters-card__actions">
                                <Button variant="outline-secondary" size="sm" onClick={handleClearFilters}>
                                    Réinitialiser
                                </Button>
                                <Button variant="primary" size="sm" onClick={handleApplyFilters}>
                                    Appliquer les filtres
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body className="partner-filters-card__body">
                            <Row className="g-3 partner-filters-row">
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Email</Form.Label>
                                        <Form.Control
                                            type="email"
                                            name="email"
                                            value={filters.email}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Email du partenaire"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Label>Nom de société</Form.Label>
                                    <PartnerCompanyAutocomplete
                                        value={filters.company_name}
                                        onChange={(companyName) => setFilters((prev) => ({ ...prev, company_name: companyName }))}
                                        onInputChange={setCompanyPartnerSearch}
                                        options={partnerCompanyOptions}
                                        loading={filterPartnersLoading}
                                        error={filterPartnersError}
                                        onKeyDown={handleKeyPress}
                                        placeholder="Rechercher un partenaire…"
                                    />
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Prénom</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="first_name"
                                            value={filters.first_name}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Prénom"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nom</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="last_name"
                                            value={filters.last_name}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Nom"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {partners.loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Chargement des partenaires...</p>
                        </div>
                    ) : currentPartners.length === 0 ? (
                        <div className="text-center py-5">
                            <FiLink size={48} className="text-muted mb-3" />
                            <p className="text-muted">Aucun partenaire disponible</p>
                        </div>
                    ) : (
                        <div className="table-responsive table-scroll partner-table-wrapper">
                            <Table hover className="admin-table partner-data-table">
                                <thead>
                                    <tr>
                                        <th>Entreprise</th>
                                        <th>Nom</th>
                                        <th>Prénom</th>
                                        <th>Email</th>
                                        <th>Téléphone</th>
                                        <th>Code utilisateur</th>
                                        <th className="text-end col-actions">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentPartners.length > 0 ? (
                                        currentPartners.map((partner, index) => {
                                            const partnerId = partner.user_uuid || partner.id || partner.partner_id || `partner-${index}`;
                                            return (
                                                <tr key={partnerId}>
                                                    <td>
                                                        <strong>{partner.company_name || partner.name || 'N/A'}</strong>
                                                    </td>
                                                    <td>{partner.last_name || 'N/A'}</td>
                                                    <td>{partner.first_name || 'N/A'}</td>
                                                    <td>{partner.email || 'N/A'}</td>
                                                    <td>{partner.phone_number || partner.phone || 'N/A'}</td>
                                                    <td>{partner.user_code || partner.code || 'N/A'}</td>
                                                    <td className="text-end col-actions">
                                                        <Button
                                                            variant="outline-info"
                                                            size="sm"
                                                            className="me-2"
                                                            onClick={() => handleDetails(partnerId)}
                                                            title="Détails"
                                                            aria-label="Détails"
                                                        >
                                                            <FiEye />
                                                        </Button>
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            className="me-2"
                                                            onClick={() => handleEdit(partnerId)}
                                                            title="Modifier"
                                                            aria-label="Modifier"
                                                        >
                                                            <FiEdit />
                                                        </Button>
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={() => handleDelete(partnerId)}
                                                            title="Supprimer"
                                                            aria-label="Supprimer"
                                                        >
                                                            <FiTrash2 />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className="text-center">
                                                Aucun partenaire trouvé
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    {totalItems > 0 && totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-4 partner-pagination-bar">
                            <div className="text-muted">
                                Affichage de {indexOfFirstItem + 1} à {indexOfLastItem} sur {totalItems} partenaire{totalItems > 1 ? 's' : ''}
                            </div>
                            <Pagination className="mb-0">
                                <Pagination.First
                                    onClick={() => handlePageChange(1)}
                                    disabled={currentPage === 1}
                                />
                                <Pagination.Prev
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                />
                                {(() => {
                                    const { startPage, endPage } = getPaginationRange();
                                    const pages = [];

                                    if (startPage > 1) {
                                        pages.push(
                                            <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                                                1
                                            </Pagination.Item>
                                        );
                                        if (startPage > 2) {
                                            pages.push(<Pagination.Ellipsis key="ellipsis-start" />);
                                        }
                                    }

                                    for (let i = startPage; i <= endPage; i++) {
                                        pages.push(
                                            <Pagination.Item
                                                key={i}
                                                active={i === currentPage}
                                                onClick={() => handlePageChange(i)}
                                            >
                                                {i}
                                            </Pagination.Item>
                                        );
                                    }

                                    if (endPage < totalPages) {
                                        if (endPage < totalPages - 1) {
                                            pages.push(<Pagination.Ellipsis key="ellipsis-end" />);
                                        }
                                        pages.push(
                                            <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                                                {totalPages}
                                            </Pagination.Item>
                                        );
                                    }

                                    return pages;
                                })()}
                                <Pagination.Next
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                />
                                <Pagination.Last
                                    onClick={() => handlePageChange(totalPages)}
                                    disabled={currentPage === totalPages}
                                />
                            </Pagination>
                        </div>
                    )}
                </Card.Body>
            </Card>

            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmer la suppression</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Étes-vous sûr de vouloir supprimer ce partenaire ? Cette action est irréversible.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Annuler
                    </Button>
                    <Button variant="danger" onClick={handleConfirmDelete} disabled={deleteState.loading}>
                        {deleteState.loading ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Suppression...
                            </>
                        ) : (
                            'Supprimer'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

        </div>
    );
};

export default AdminPartner;
